import {
  mandaEnElRango,
  masBaratoDe,
  masBaratoPositivo,
  NOTA_SIN_REDONDEO_FILA,
  NOTA_SIN_RANGOS_PROPUESTA,
  NOTA_SIN_REDONDEO_PROPUESTA,
  type RecorridoDelPrecio,
  type ReglaDelRecorrido,
} from '@/features/pricing-rules/lib/recorrido-del-precio';
import type {
  ComoPide,
  Cruce,
  RangoDeRegla,
  RangoPisado,
  RedondeoAplicado,
  SalidasSinPrecio,
} from '@/features/pricing-rules/types/pricing-rules.types';
import type { PriceProposalRow } from '../types/price-proposals.types';
import { formatoPorcentaje, formatoPrecio } from '@/lib/utils/formato-precio';

/**
 * Los adaptadores de Análisis y de las propuestas a la explicación única
 * (spec 225, pieza 1): arman el mismo `RecorridoDelPrecio` que la foto
 * (`desdeFilaSimulada`), así las tres fuentes dicen la misma cuenta
 * (`explicacion-del-precio.test.ts`, «coherencia»). Puros.
 */

const numero = (v: unknown): number | null =>
  typeof v === 'number' && Number.isFinite(v) ? v : null;

const conSuPapel = (
  rangos: RangoDeRegla[],
  limitantes: string[],
  precioDeLasReglas: number | null
): ReglaDelRecorrido[] =>
  rangos.map((r) => ({
    ...r,
    manda: mandaEnElRango(r, rangos, limitantes, precioDeLasReglas),
    probando: false,
  }));

/**
 * Una estrategia de `POST /api/smart-pricing/simulate-strategies` (la tarjeta
 * del panel de comparación de Análisis). El `snapshot` trae `rangos`, `cruce`,
 * `limitantes`, `redondeo` y `comoPide` desde el spec 225; una respuesta de
 * antes (o sin snapshot) cae a «sin dibujo» y lo dice.
 */
export function desdeSimulacion(args: {
  metodo: string;
  precioHoy: number;
  precio: number | null;
  costo: number | null;
  snapshot: unknown;
}): RecorridoDelPrecio {
  const s = (args.snapshot ?? {}) as Record<string, unknown>;
  const sinPrecioPosible = s.sinPrecioPosible === true;
  const rangos = Array.isArray(s.rangos) ? (s.rangos as RangoDeRegla[]) : null;
  const limitantes = Array.isArray(s.limitantes) ? (s.limitantes as string[]) : [];
  const estado = sinPrecioPosible
    ? 'SIN_PRECIO_POSIBLE'
    : args.precio == null
      ? 'SIN_CALCULO'
      : 'CALCULADO';
  const notas: string[] = [];
  if (estado === 'CALCULADO' && !rangos) notas.push('Este cálculo no trajo qué deja cada regla.');
  else if (estado === 'CALCULADO' && !('redondeo' in s)) notas.push(NOTA_SIN_REDONDEO_FILA);
  const redondeo = (s.redondeo ?? null) as RedondeoAplicado | null;
  // Lo que dejan las reglas antes de redondear, para saber cuál rango manda
  // en una regla con dos límites (el mismo criterio que `desdeFilaSimulada`).
  const precioDeLasReglas = redondeo ? redondeo.desde : args.precio;
  return {
    estado,
    porQue:
      estado === 'SIN_CALCULO'
        ? 'Este método no pudo calcular un precio para este producto.'
        : null,
    hoy: args.precioHoy,
    costo: args.costo,
    pedido: {
      origen: 'ESTRATEGIA',
      estrategia: args.metodo,
      como: (s.comoPide ?? null) as ComoPide | null,
      usaMargenPropio: false,
      explicacionDeVuelta: null,
      precio: numero(s.precioPedido) ?? numero(s.rawPriceBeforeRules),
    },
    reglas: rangos ? conSuPapel(rangos, limitantes, precioDeLasReglas) : null,
    // Lote B2: las que no rigen. Una simulación de antes no las trae: ninguna.
    pisadas: rangos && Array.isArray(s.pisados) ? (s.pisados as RangoPisado[]) : [],
    cruce: rangos && s.cruce ? (s.cruce as Cruce) : null,
    reglaQueLimito: null,
    redondeo: estado === 'CALCULADO' && redondeo ? { ...redondeo, probando: false } : null,
    queda: estado === 'CALCULADO' ? args.precio : null,
    seSirve: null,
    salidas: sinPrecioPosible && Array.isArray(s.salidas) ? (s.salidas as SalidasSinPrecio) : null,
    elegida: null,
    comparacion: null,
    notas,
    mercado: null,
    masBarato:
      masBaratoPositivo(masBaratoDe(s.comoPide as ComoPide | null | undefined)) ??
      masBaratoPositivo(numero(s.competitorMin)),
    // La simulación de Análisis no trae el nombre: la frase va sin sujeto.
    producto: null,
  };
}

/**
 * Auditoría del 28-sep (C10): una sin precio posible ya resuelta dice qué salida
 * se eligió y a qué precio, el congelado en `salidas`. Si la clave no está en
 * la lista, no se afirma nada.
 */
function salidaElegida(f: PriceProposalRow): RecorridoDelPrecio['elegida'] {
  if (!f.salidaElegida || f.status === 'PENDING') return null;
  const s = (f.salidas ?? []).find((x) => x.clave === f.salidaElegida);
  return s ? { clave: s.clave, precio: s.precio } : null;
}

/**
 * Una fila de Por decidir o del historial (`aFila` del backend, que también
 * es la forma de las propuestas resueltas). Las viejas no traen rangos: se
 * nombra la regla que limitó, como se guardó. Las de antes del spec 225 no
 * guardaron el redondeo: se dice, no se infiere. La vuelta del plan 2
 * (`triggerKind` EXPIRY) no la pidió la estrategia: vuelve a su precio de antes.
 */
export function desdeFilaDeLaBandeja(f: PriceProposalRow): RecorridoDelPrecio {
  const sinPrecioPosible = f.sinPrecioPosible === true;
  const rangos = f.rangos ?? null;
  const estado = sinPrecioPosible ? 'SIN_PRECIO_POSIBLE' : 'CALCULADO';
  const esVuelta = f.triggerKind === 'EXPIRY';
  const notas: string[] = [];
  // Ronda final de R4 (QA): una pendiente cuyo costo cambió se lee con el costo de
  // HOY (el que importa para decidir); el margen de cuando se calculó queda como
  // nota. Resueltas y sin costo de hoy: el congelado, como siempre.
  const conCostoDeHoy =
    f.status === 'PENDING' && f.costoCambio === true && f.costoHoy != null && f.costo != null;
  if (conCostoDeHoy && f.marginPercent != null)
    notas.push(
      `Al calcular, con el costo de entonces (${formatoPrecio(f.costo!)}), ganaba ${formatoPorcentaje(f.marginPercent)}.`
    );
  if (!rangos) notas.push(NOTA_SIN_RANGOS_PROPUESTA);
  else if (estado === 'CALCULADO' && f.redondeo === undefined)
    notas.push(NOTA_SIN_REDONDEO_PROPUESTA);
  // Lo que dejan las reglas antes de redondear (mismo criterio que los otros
  // dos adaptadores): cuál rango manda en una regla con dos límites.
  const precioDeLasReglas =
    estado === 'CALCULADO' ? (f.redondeo ? f.redondeo.desde : f.priceAfter) : null;
  return {
    estado,
    porQue: null,
    hoy: f.priceBefore,
    // Ruling del 26-sep: aprobada con el costo ya borrado, el backend deja
    // `marginPercent` en null a propósito; el margen no se recalcula con el
    // costo congelado (auditoría C2).
    costo: conCostoDeHoy ? f.costoHoy! : f.marginPercent == null ? null : (f.costo ?? null),
    pedido: esVuelta
      ? {
          origen: 'VUELTA',
          estrategia: null,
          como: null,
          usaMargenPropio: false,
          explicacionDeVuelta:
            f.explicacionDeVuelta ?? 'Volvió a su precio de antes al vencer su estrategia.',
          precio: f.strategyPrice ?? null,
        }
      : f.origenDelPedido === 'HOY'
        ? // Lote C1 (punto 8): sin estrategia, el recálculo partió del precio de hoy.
          {
            origen: 'HOY',
            estrategia: null,
            como: null,
            usaMargenPropio: false,
            explicacionDeVuelta: null,
            precio: f.strategyPrice ?? f.priceBefore,
          }
        : {
            origen: 'ESTRATEGIA',
            estrategia: f.strategyName,
            como: f.comoPide ?? null,
            usaMargenPropio: f.usaMargenPropio === true,
            explicacionDeVuelta: null,
            precio: f.strategyPrice ?? null,
          },
    reglas: rangos
      ? conSuPapel(
          rangos,
          f.limitantes ?? (f.heldByRuleId ? [f.heldByRuleId] : []),
          precioDeLasReglas
        )
      : null,
    // Lote B2: las que no regían. Las de antes no lo guardaron (null): ninguna que decir.
    pisadas: rangos ? (f.pisados ?? []) : [],
    cruce: rangos ? (f.cruce ?? null) : null,
    reglaQueLimito: rangos ? null : f.heldByRuleName,
    redondeo: estado === 'CALCULADO' && f.redondeo ? { ...f.redondeo, probando: false } : null,
    queda: estado === 'CALCULADO' ? f.priceAfter : null,
    seSirve: null,
    salidas: sinPrecioPosible ? (f.salidas ?? null) : null,
    elegida: sinPrecioPosible ? salidaElegida(f) : null,
    comparacion: null,
    notas,
    // Lote B1 (pieza 2): congelado en la propuesta. null en las de antes.
    mercado: f.mercado ?? null,
    masBarato: masBaratoPositivo(masBaratoDe(f.comoPide)) ?? masBaratoPositivo(f.precioMasBarato),
    // Un nombre vacío cuenta como ausente, igual que en la foto (`name || null`; informe 229).
    producto: f.productName || null,
  };
}
